import React from 'react';
import { Box, Item, Span, Image } from '../../library/react-html-email';
import EmailProductCard from './EmailProductCard';

/**
 * EmailApplicationSection - Email-safe application section component
 * Displays a product application category (Engine, Transmission, etc.)
 * with specifications and all associated products
 */
const EmailApplicationSection = ({
  application,
  equipment,
  t,
  assetPath,
  getProductData,
  query = {}
}) => {
  // Strip HTML tags from description
  const stripHtmlTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  const { name, capacity, note_refs, products = [], c_option } = application;
  const capacityUnit = equipment?.model?.capacity_unit || '';

  // Build title with selected option if available
  const sectionTitle = c_option ? `${name} - ${c_option}` : name;

  // Find OCI (Oil Change Interval) for this application
  const ociData = equipment?.change_interval?.find(
    (i) => Number(i.application_id) === application.id
  );

  let ociText = '';
  if (ociData && ociData.intervals) {
    if (ociData.intervals.length === 1) {
      ociText = ociData.intervals[0].value + ociData.intervals[0].unit;
    } else if (ociData.intervals.length > 1) {
      ociText = ociData.intervals[0].value + ociData.intervals[0].unit +
        ' / ' + ociData.intervals[1].value + ociData.intervals[1].unit;
    }
  }

  return (
    <Box width="600" align="center" style={{ marginBottom: '24px' }}>
      {/* Section Title - Left Aligned on Gray */}
      <Item style={{ paddingBottom: '16px', textAlign: 'left' }}>
        <table cellPadding="0" cellSpacing="0" border="0" align="left">
          <tr>
            <td style={{ verticalAlign: 'middle' }}>
              <Span
                fontSize={20}
                color="#202020"
                style={{
                  fontWeight: '600',
                  fontFamily: 'Arial, sans-serif',
                  display: 'inline'
                }}
              >
                {sectionTitle}
              </Span>
            </td>
            <td style={{ verticalAlign: 'middle', paddingLeft: '12px' }}>
              <table cellPadding="0" cellSpacing="0" border="0">
                <tr>
                  <td
                    align="center"
                    valign="middle"
                    style={{
                      width: '24px',
                      height: '24px',
                      textAlign: 'center',
                      backgroundColor: 'rgba(32, 32, 32, 0.1)',
                      borderRadius: '50%'
                    }}
                  >
                    <Span fontSize={14} color="#202020" style={{ fontWeight: '600', fontFamily: 'Arial, sans-serif', lineHeight: '24px' }}>
                      {products.length}
                    </Span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </Item>

      {/* Specifications (Capacity, OCI) on Gray */}
      {(capacity || ociText) && (
        <Item style={{ paddingBottom: '20px' }}>
          <table cellPadding="0" cellSpacing="0" border="0" width="100%">
            <tr>
              {/* Capacity */}
              {capacity && (
                <td style={{ verticalAlign: 'top', paddingRight: '16px' }}>
                  <table cellPadding="0" cellSpacing="0" border="0">
                    <tr>
                      <td style={{ verticalAlign: 'top', paddingRight: '8px' }}>
                        <Image
                          src={assetPath + 'images/email/capacity.png'}
                          alt="Capacity"
                          width={24}
                          height={24}
                          style={{
                            display: 'block',
                            opacity: '0.75'
                          }}
                        />
                      </td>
                      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        <Span fontSize={16} color="#202020" style={{ fontFamily: 'Arial, sans-serif', lineHeight: '24px' }}>
                          <Span color="#202020" style={{ opacity: '0.75' }}>{t('vehicle_spec.capacity')}&nbsp;</Span>
                          <Span style={{ fontWeight: '600' }}>{capacity} {capacityUnit}</Span>
                        </Span>
                      </td>
                    </tr>
                  </table>
                </td>
              )}

              {/* OCI */}
              {ociText && (
                <td style={{ verticalAlign: 'top' }}>
                  <table cellPadding="0" cellSpacing="0" border="0">
                    <tr>
                      <td style={{ verticalAlign: 'top', paddingRight: '8px' }}>
                        <Image
                          src={assetPath + 'images/email/drain.png'}
                          alt="OCI"
                          width={24}
                          height={24}
                          style={{
                            display: 'block',
                            opacity: '0.75'
                          }}
                        />
                      </td>
                      <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        <Span fontSize={16} color="#202020" style={{ fontFamily: 'Arial, sans-serif', lineHeight: '24px' }}>
                          <Span color="#202020" style={{ opacity: '0.75' }}>{t('vehicle_spec.oci')}&nbsp;</Span>
                          <Span style={{ fontWeight: '600' }}>{ociText}</Span>
                        </Span>
                      </td>
                    </tr>
                  </table>
                </td>
              )}
            </tr>
          </table>
        </Item>
      )}

      {/* Products and Notes Container - Unified White Box */}
      {(products.length > 0 || (note_refs && note_refs.length > 0)) && (
        <Item bgcolor="#ffffff" style={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
          <Box width="600" bgcolor="#ffffff" align="center" style={{ backgroundColor: '#ffffff' }}>
            <Item bgcolor="#ffffff" style={{ backgroundColor: '#ffffff' }}>
              {/* Products Loop */}
              <Box width="100%" bgcolor="#ffffff" style={{ backgroundColor: '#ffffff' }}>
                {products.map((product) => {
                  const productData = getProductData(product.gpn);
                  const defaultProductImage = assetPath + 'images/email/product-default.png';
                  const preferredPac = (productData.pacs && productData.pacs.find((pac) => pac.volume === '4L')) || null;
                  const fallbackPac = productData.pacs && productData.pacs[0];

                  let pacShot = defaultProductImage;
                  if (preferredPac && preferredPac.img && preferredPac.img.trim()) {
                    pacShot = preferredPac.img;
                  } else if (fallbackPac && fallbackPac.img && fallbackPac.img.trim()) {
                    pacShot = fallbackPac.img;
                  } else if (productData.default_image_url && productData.default_image_url.trim()) {
                    pacShot = productData.default_image_url;
                  } else if (equipment && equipment.default_image_url && equipment.default_image_url.trim()) {
                    pacShot = equipment.default_image_url;
                  }

                  const description = stripHtmlTags(productData.short_description || product.short_description) || '';
                  const productName = productData.name || product.name || product.gpn || 'Product';
                  const maxDescriptionLength = 200;
                  let productDescription = description;
                  if (productDescription.length > maxDescriptionLength) {
                    productDescription = productDescription.substring(0, maxDescriptionLength) + '...';
                  }
                  const productLink = productData.product_page_link || product.product_page_link || null;

                  return (
                    <EmailProductCard
                      key={product.gpn}
                      product={{
                        image: pacShot,
                        name: productName,
                        description: productDescription,
                        detailsLink: productLink
                      }}
                      assetPath={assetPath}
                      query={query}
                      links={productData.links}
                      t={t}
                    />
                  );
                })}
              </Box>

              {/* Inline Section Notes inside the White Box */}
              {note_refs && note_refs.length > 0 && (
                <Item bgcolor="#ffffff" style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px', backgroundColor: '#ffffff' }}>
                  {/* Spacer for top margin */}
                  <Box width="100%" bgcolor="#ffffff" style={{ backgroundColor: '#ffffff' }}>
                    <Item bgcolor="#ffffff" style={{ height: '10px', backgroundColor: '#ffffff' }}>&nbsp;</Item>
                  </Box>
                  <Box width="100%" align="left" bgcolor="#ffffff" style={{ backgroundColor: '#ffffff' }}>
                    <Item bgcolor="#ffffff" style={{ borderTop: '1px solid #ddd', paddingTop: '20px', paddingBottom: '10px', backgroundColor: '#ffffff' }}>
                      <Span fontSize={20} color="#202020" style={{ fontWeight: '600', display: 'block' }}>
                        {t('result_notes.notes')}
                      </Span>
                    </Item>
                    {note_refs.map((ref) => {
                      const note = (equipment?.application_notes || []).find(
                        (n) => n.note_index === ref.note_index
                      );
                      return note ? (
                        <Item key={note.id} style={{ paddingBottom: '8px', backgroundColor: '#ffffff' }} bgcolor="#ffffff">
                          <Span fontSize={16} color="#202020" style={{ lineHeight: '1.5', fontFamily: 'Arial, sans-serif', opacity: '0.9' }}>
                            {note.text}
                          </Span>
                        </Item>
                      ) : null;
                    })}
                  </Box>
                </Item>
              )}
            </Item>
          </Box>
        </Item>
      )}
    </Box>
  );
};

export default EmailApplicationSection;
